<template>
  <AkAlert
    v-if="isUploading || uploadProgress.failed > 0 || uploadProgress.uploaded > 0"
    class="media-upload-alert"
    :class="{ 'media-upload-alert--loading': isUploading }"
    :type="AlertType.Info"
    :title="isUploading ? uploadProgressTitle : t('brand.account.mediaLibrary.report.title.report')"
    hide-icon
  >
    <div v-if="isUploading">
      <LoaderIcon small />
      <AkParagraph class="media-upload-alert__loading-text">
        {{ uploadingText || t('brand.account.mediaLibrary.report.content.loading') }}
      </AkParagraph>
    </div>
    <div class="media-upload-alert__status-container">
      <div class="media-upload-alert__status-item">
        <AkIcon
          size="md"
          symbol="check2-circle"
          class="status-item-icon"
        /><div>
          {{ t('brand.account.mediaLibrary.report.content.uploaded', { count: uploadProgress.uploaded }) }}
        </div>
      </div>
      <div
        v-if="uploadProgress.failed > 0"
        class="media-upload-alert__status-item media-upload-alert__status-item--error"
      >
        <AkIcon
          size="md"
          symbol="exclamation-triangle"
          class="status-item-icon"
        /><div>
          {{ t('brand.account.mediaLibrary.report.content.failed', { count: uploadProgress.failed }) }}
        </div>
      </div>
    </div>

    <div
      v-if="uploadProgress.failed > 0"
      class="media-upload-alert__failed-section"
    >
      <AkLink @click="toggleFailedFilesVisible">
        {{ toggleLinkText }}
      </AkLink>

      <div
        v-if="failedFilesVisible"
        class="media-upload-alert__failed-files"
      >
        <div
          v-for="(failedMedia, index) in failedUploadMedia"
          :key="index"
          class="failed-file-item"
        >
          <AkTooltipInformation
            :auto-hide="true"
            :content="failedMedia.error"
            placement="top"
            trigger="hover"
            type="success"
          >
            <div>{{ failedMedia.file.name }}</div>
          </AkTooltipInformation>
        </div>
      </div>
    </div>
  </AkAlert>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import LoaderIcon from '@monolith/legacy/components/loader-icon.vue';
import { AkAlert, AkParagraph, AlertType, AkIcon, AkTooltipInformation, AkLink } from '@ankorstore/design-system';
import { useMediaLibrary } from '../../composables/use-media-library';

withDefaults(
  defineProps<{
    uploadingText?: string;
  }>(),
  {
    uploadingText: '',
  }
);

const { t } = useI18n();
const { isUploading, uploadProgress, failedUploadMedia } = useMediaLibrary();
const failedFilesVisible = ref(false);

const uploadProgressTitle = computed(() => {
  return `${uploadProgress.value.current}/${uploadProgress.value.total} ${t('brand.account.mediaLibrary.report.title.loading')}`;
});

const toggleFailedFilesVisible = () => {
  failedFilesVisible.value = !failedFilesVisible.value;
};

const toggleLinkText = computed(() => failedFilesVisible.value ? t('brand.account.mediaLibrary.report.hideFailedFiles') : t('brand.account.mediaLibrary.report.showFailedFiles'));
</script>

<style lang="scss" scoped>
.media-upload-alert {
  @apply ds-text-left ds-mb-4;

  &__loading-text {
    @apply ds-text-neutral-800 ds-mb-2;
  }

  &__status-container {
    @apply ds-flex ds-gap-4 ds-flex-wrap ds-text-sm;
  }

  &__status-item {
    @apply ds-flex ds-items-center ds-gap-2;

    &--error {
      @apply ds-text-error-700;
    }

    .status-item-icon {
      @apply ds-text-base;
    }
  }

  &__failed-section {
    @apply ds-mt-1.5;
  }

  &__failed-files {
    @apply ds-mt-2 ds-flex ds-flex-wrap;
  }

  &--loading.ak-alert {
    @apply ds-relative ds-pl-7 #{!important};
  }

  .loader-icon {
    @apply ds-absolute ds-top-4.5 ds-left-4;
  }

  .failed-file-item {
    @apply ds-flex;

    &:not(:last-child)::after {
      content: ", ";
      margin-right: 0.25rem;
    }
  }
}
</style>
